import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    clientAdjustmentApi,
    ClientAdjustmentRecord,
    BulkClientAdjustmentDto,
} from "@/lib/api";
import { toast } from "sonner";
import { ClientRowData } from "./types";
import { parseNum, applyPercentMask } from "./utils";

export function useClientAdjustments() {
    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
    const [cycleYear, setCycleYear] = useState<number>(currentYear);

    const isPastYear = cycleYear < currentYear;

    // Filtros
    const [search, setSearch] = useState<string>("");
    const [groupFilter, setGroupFilter] = useState<string>("");
    const [stateFilter, setStateFilter] = useState<string>("");

    const queryClient = useQueryClient();

    // Estado principal de clientes e edições
    const [clients, setClients] = useState<ClientRowData[]>([]);
    const [savedAdjustments, setSavedAdjustments] = useState<Record<number, ClientAdjustmentRecord>>({});
    const [allAdjustmentsMap, setAllAdjustmentsMap] = useState<Record<number, ClientAdjustmentRecord[]>>({});
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [focusedInput, setFocusedInput] = useState<number | 'bulk' | null>(null);
    const [individualComments, setIndividualComments] = useState<Record<number, string>>({});
    const [selectedMap, setSelectedMap] = useState<Record<number, boolean>>({});
    const [bulkPercent, setBulkPercent] = useState<string>("");

    // Modais
    const [showSave, setShowSave] = useState<boolean>(false);
    const [saveComment, setSaveComment] = useState<string>("");

    const [showRemove, setShowRemove] = useState<boolean>(false);
    const [removeTarget, setRemoveTarget] = useState<ClientRowData | null>(null);
    const [removeComment, setRemoveComment] = useState<string>("");

    const [showHistory, setShowHistory] = useState<boolean>(false);
    const [historyTarget, setHistoryTarget] = useState<ClientRowData | null>(null);

    // --- CARREGAMENTO DOS DADOS COM USEQUERY ---
    const {
        data: queryData,
        isLoading,
    } = useQuery({
        queryKey: ["clientAdjustmentsData", cycleYear],
        queryFn: async () => {
            const prevYear = cycleYear - 1;

            const fetchYearSafe = async (year: number) => {
                try {
                    return await clientAdjustmentApi.getAdjustmentsByYear(year);
                } catch (err) {
                    console.warn(`Aviso: não foi possível carregar reajustes do ciclo ${year}:`, err);
                    return [];
                }
            };

            const [clientList, currentYearAdjustments, prevYearAdjustments] = await Promise.all([
                clientAdjustmentApi.getAllClients().catch((err) => {
                    console.error("Erro ao carregar lista de clientes:", err);
                    toast.error("Erro ao carregar lista de clientes.");
                    return [];
                }),
                fetchYearSafe(cycleYear),
                fetchYearSafe(prevYear),
            ]);

            const newSavedAdjustments: Record<number, ClientAdjustmentRecord> = {};
            const currentAdjMap: Record<number, ClientAdjustmentRecord> = {};
            const prevAdjMap: Record<number, ClientAdjustmentRecord> = {};

            currentYearAdjustments.forEach((adj) => {
                newSavedAdjustments[adj.clientId] = adj;
                currentAdjMap[adj.clientId] = adj;
            });

            prevYearAdjustments.forEach((adj) => {
                prevAdjMap[adj.clientId] = adj;
            });

            const fullClientData: ClientRowData[] = clientList.map((c) => {
                const adjObj: Record<number, number> = {};
                if (currentAdjMap[c.clientId]) {
                    adjObj[cycleYear] = currentAdjMap[c.clientId].adjustment;
                }
                if (prevAdjMap[c.clientId]) {
                    adjObj[prevYear] = prevAdjMap[c.clientId].adjustment;
                }
                return {
                    ...c,
                    adjustments: adjObj,
                };
            });

            const initialDrafts: Record<number, string> = {};
            fullClientData.forEach((c) => {
                const saved = newSavedAdjustments[c.clientId];
                if (saved && saved.adjustment !== undefined && saved.adjustment !== null) {
                    initialDrafts[c.clientId] = saved.adjustment.toFixed(2).replace(".", ",");
                }
            });

            return {
                clients: fullClientData,
                savedAdjustments: newSavedAdjustments,
                initialDrafts,
            };
        },
    });

    useEffect(() => {
        if (queryData) {
            setClients(queryData.clients);
            setSavedAdjustments(queryData.savedAdjustments);
            setAllAdjustmentsMap(queryData.allAdjustmentsMap || {});
            setDrafts(queryData.initialDrafts);
            setIndividualComments({});
            setSelectedMap({});
        }
    }, [queryData]);

    // Query de histórico
    const {
        data: historyRows = [],
        isLoading: isHistoryLoading,
    } = useQuery({
        queryKey: ["clientAdjustmentsHistory", historyTarget?.clientId],
        queryFn: () => clientAdjustmentApi.getAdjustments(historyTarget!.clientId),
        enabled: !!historyTarget && showHistory,
    });

    // Dropdowns de filtros
    const groupOptions = useMemo(() => {
        const set = new Set<string>();
        clients.forEach((c) => {
            if (c.clientCustomerGroup) set.add(c.clientCustomerGroup);
        });
        return Array.from(set).sort();
    }, [clients]);

    const stateOptions = useMemo(() => {
        const set = new Set<string>();
        clients.forEach((c) => {
            if (c.clientState) set.add(c.clientState);
        });
        return Array.from(set).sort();
    }, [clients]);

    // Clientes filtrados
    const filteredClients = useMemo(() => {
        const q = search.trim().toLowerCase();
        return clients.filter((c) => {
            if (
                q &&
                !(
                    (c.clientCodeCnx || "") +
                    (c.clientCompanyName || "") +
                    (c.clientFantasyName || "") +
                    (c.clientCnpj || "") +
                    (c.clientCity || "")
                )
                    .toLowerCase()
                    .includes(q)
            ) {
                return false;
            }
            if (groupFilter && c.clientCustomerGroup !== groupFilter) return false;
            if (stateFilter && c.clientState !== stateFilter) return false;
            return true;
        });
    }, [clients, search, groupFilter, stateFilter]);

    const hasActiveFilters = Boolean(search.trim() || groupFilter || stateFilter);

    const handleClearFilters = () => {
        setSearch("");
        setGroupFilter("");
        setStateFilter("");
    };

    // Seleção
    const selectedIds = useMemo(() => {
        return Object.keys(selectedMap)
            .filter((id) => selectedMap[Number(id)])
            .map(Number);
    }, [selectedMap]);

    const selectedCount = selectedIds.length;

    const allSelected = useMemo(() => {
        if (filteredClients.length === 0) return false;
        return filteredClients.every((c) => selectedMap[c.clientId]);
    }, [filteredClients, selectedMap]);

    const handleToggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const next: Record<number, boolean> = { ...selectedMap };
        filteredClients.forEach((c) => {
            next[c.clientId] = checked;
        });
        setSelectedMap(next);
    };

    const handleToggleSelectRow = (clientId: number) => {
        setSelectedMap((prev) => ({
            ...prev,
            [clientId]: !prev[clientId],
        }));
    };

    // Alterações pendentes
    const dirtyClients = useMemo(() => {
        if (isPastYear) return [];
        return clients.filter((c) => {
            const draftStr = drafts[c.clientId];
            if (draftStr === undefined || draftStr === "") return false;
            const n = parseNum(draftStr);
            if (isNaN(n) || n < 0 || n > 100) return false;

            const saved = savedAdjustments[c.clientId];
            const savedVal = saved ? saved.adjustment : null;
            return savedVal === null || Number(savedVal.toFixed(2)) !== Number(n.toFixed(2));
        });
    }, [clients, drafts, savedAdjustments, isPastYear]);

    const dirtyCount = dirtyClients.length;

    // Reajuste médio do ciclo
    const avgAdjustmentStr = useMemo(() => {
        let sum = 0;
        let count = 0;

        filteredClients.forEach((c) => {
            const draftStr = drafts[c.clientId];
            if (draftStr !== undefined && draftStr !== "") {
                const n = parseNum(draftStr);
                if (!isNaN(n) && n >= 0 && n <= 100) {
                    sum += n;
                    count++;
                    return;
                }
            }

            const saved = savedAdjustments[c.clientId];
            if (saved) {
                sum += saved.adjustment;
                count++;
            }
        });

        if (count === 0) return "—";
        const avg = sum / count;
        return avg.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%";
    }, [filteredClients, drafts, savedAdjustments]);

    // Edição individual
    const handleDraftInput = (clientId: number, value: string) => {
        const masked = applyPercentMask(value);
        setDrafts((prev) => ({
            ...prev,
            [clientId]: masked,
        }));
    };

    const handleDraftFocus = (clientId: number, e: React.FocusEvent<HTMLInputElement>) => {
        setFocusedInput(clientId);
        e.target.select();
    };

    const handleDraftBlur = (clientId: number) => {
        setFocusedInput(null);
        const currentStr = drafts[clientId];
        if (!currentStr) return;
        const num = parseNum(currentStr);
        if (!isNaN(num) && num >= 0 && num <= 100) {
            setDrafts((prev) => ({
                ...prev,
                [clientId]: num.toFixed(2).replace(".", ","),
            }));
        }
    };

    // Preenchimento em lote
    const handleBulkPercentChange = (value: string) => {
        setBulkPercent(applyPercentMask(value));
    };

    const handleBulkPercentBlur = () => {
        setFocusedInput(null);
        if (!bulkPercent) return;
        const num = parseNum(bulkPercent);
        if (!isNaN(num) && num >= 0 && num <= 100) {
            setBulkPercent(num.toFixed(2).replace(".", ","));
        }
    };

    const handleApplyBulkPercent = () => {
        const n = parseNum(bulkPercent);
        if (isNaN(n) || n < 0 || n > 100) {
            toast.error("Informe um valor percentual válido entre 0,00 e 100,00.");
            return;
        }

        if (selectedCount === 0) {
            toast.warning("Selecione ao menos um cliente para aplicar em lote.");
            return;
        }

        const valStr = n.toFixed(2).replace(".", ",");
        setDrafts((prev) => {
            const next = { ...prev };
            selectedIds.forEach((id) => {
                next[id] = valStr;
            });
            return next;
        });

        toast.success(`Reajuste de ${valStr}% aplicado a ${selectedCount} cliente(s) selecionados.`);
    };

    // Descartar alterações
    const handleDiscardChanges = () => {
        const resetDrafts: Record<number, string> = {};
        clients.forEach((c) => {
            const saved = savedAdjustments[c.clientId];
            if (saved && saved.adjustment !== undefined && saved.adjustment !== null) {
                resetDrafts[c.clientId] = saved.adjustment.toFixed(2).replace(".", ",");
            }
        });
        setDrafts(resetDrafts);
        setIndividualComments({});
        toast.info("Alterações pendentes descartadas.");
    };

    // Mutações
    const bulkUpdateMutation = useMutation({
        mutationFn: (payload: BulkClientAdjustmentDto) => clientAdjustmentApi.bulkUpdate(payload),
        onSuccess: (res) => {
            const resData = (res as any)?.data || res;
            toast.success(resData?.message || `Reajuste(s) salvos com sucesso!`);
            setShowSave(false);
            setSaveComment("");
            queryClient.invalidateQueries({ queryKey: ["clientAdjustmentsData"] });
        },
        onError: (err: any) => {
            console.error("Erro ao salvar em lote:", err);
            toast.error(err?.response?.data?.message || "Erro ao salvar reajustes.");
        },
    });

    const deleteAdjustmentMutation = useMutation({
        mutationFn: ({ clientId, year, comment }: { clientId: number; year: number; comment?: string }) =>
            clientAdjustmentApi.deleteAdjustment(clientId, year, comment),
        onSuccess: (_, variables) => {
            toast.success(`Reajuste do ciclo ${variables.year} removido para ${removeTarget?.clientFantasyName}.`);
            setShowRemove(false);
            setRemoveTarget(null);
            setRemoveComment("");
            queryClient.invalidateQueries({ queryKey: ["clientAdjustmentsData"] });
        },
        onError: (err: any) => {
            console.error("Erro ao remover reajuste:", err);
            toast.error(err?.response?.data?.message || "Erro ao remover reajuste.");
        },
    });

    const isSubmitting = bulkUpdateMutation.isPending || deleteAdjustmentMutation.isPending;

    const handleOpenSave = () => {
        if (dirtyCount === 0) {
            toast.warning("Nenhuma alteração pendente para salvar.");
            return;
        }
        setSaveComment(`Reajuste anual contratual ciclo ${cycleYear}`);
        setShowSave(true);
    };

    const handleConfirmSave = () => {
        if (!saveComment.trim()) {
            toast.error("O comentário/justificativa é obrigatório.");
            return;
        }

        const items = dirtyClients.map((c) => {
            const val = parseNum(drafts[c.clientId]);
            const customComment = individualComments[c.clientId]?.trim();
            return {
                clientId: c.clientId,
                adjustment: val,
                comment: customComment ? customComment : undefined,
            };
        });

        const payload: BulkClientAdjustmentDto = {
            cycleYear,
            comment: saveComment.trim(),
            items,
        };

        bulkUpdateMutation.mutate(payload);
    };

    const handleOpenRemove = (client: ClientRowData) => {
        setRemoveTarget(client);
        setRemoveComment(`Remoção de reajuste do ciclo ${cycleYear}`);
        setShowRemove(true);
    };

    const handleConfirmRemove = () => {
        if (!removeTarget || !removeComment.trim()) {
            toast.error("A justificativa de remoção é obrigatória.");
            return;
        }

        deleteAdjustmentMutation.mutate({
            clientId: removeTarget.clientId,
            year: cycleYear,
            comment: removeComment.trim(),
        });
    };

    const handleOpenHistory = (client: ClientRowData) => {
        setHistoryTarget(client);
        setShowHistory(true);
    };

    return {
        currentYear,
        yearOptions,
        cycleYear,
        setCycleYear,
        isPastYear,
        search,
        setSearch,
        groupFilter,
        setGroupFilter,
        stateFilter,
        setStateFilter,
        groupOptions,
        stateOptions,
        hasActiveFilters,
        handleClearFilters,
        clients,
        filteredClients,
        savedAdjustments,
        drafts,
        focusedInput,
        setFocusedInput,
        individualComments,
        setIndividualComments,
        selectedMap,
        selectedCount,
        allSelected,
        handleToggleSelectAll,
        handleToggleSelectRow,
        bulkPercent,
        handleBulkPercentChange,
        handleBulkPercentBlur,
        handleApplyBulkPercent,
        dirtyClients,
        dirtyCount,
        avgAdjustmentStr,
        handleDraftInput,
        handleDraftFocus,
        handleDraftBlur,
        handleDiscardChanges,
        showSave,
        setShowSave,
        saveComment,
        setSaveComment,
        handleOpenSave,
        handleConfirmSave,
        showRemove,
        setShowRemove,
        removeTarget,
        removeComment,
        setRemoveComment,
        handleOpenRemove,
        handleConfirmRemove,
        showHistory,
        setShowHistory,
        historyTarget,
        historyRows,
        isHistoryLoading,
        handleOpenHistory,
        isLoading,
        isSubmitting,
    };
}
